import { useState } from 'react';
import type { MarcaRevisaoResponse } from '@triade/contracts';
import { useDispensarMarca } from '@/api/marcas';
import { ApiError } from '@/lib/api';
import { formatarDataHora, formatarMoeda, formatarFracaoPercentual } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Marca "custo atualizado — revisar preco" (E10). Tudo vem da API: o `delta` (sugerido de hoje menos o sugerido de quando o
 * praticado foi definido), o `deltaPct`, o sugerido antes e depois. A tela so formata. Quem nao tem custo.ver recebe so
 * a marca e o novo sugerido (a API omite o lucro, a origem e o lote).
 */
export function textosMarca(m: MarcaRevisaoResponse): { variacao: string; percentual: string; antes: string; depois: string } {
  return {
    variacao: formatarMoeda(m.delta),
    percentual: m.deltaPct === null ? '—' : formatarFracaoPercentual(m.deltaPct),
    antes: formatarMoeda(m.sugeridoAntes),
    depois: formatarMoeda(m.sugeridoDepois),
  };
}

/** Pilula curta para a grade: "Custo atualizado · R$ 1,20 (5,00%)". */
export function ChipMarca({ marca, className }: { marca: MarcaRevisaoResponse; className?: string }) {
  const t = textosMarca(marca);
  return (
    <span
      data-testid="marca-revisao"
      title={`Sugerido ${t.antes} → ${t.depois} desde ${formatarDataHora(marca.desde)}. O praticado não mudou.`}
      className={cn('inline-flex items-center rounded-full bg-warning-bg px-2 text-caption leading-5 font-medium text-warning-ink', className)}
    >
      Custo atualizado · <span className="tabular ml-1">{t.variacao}</span>
      {t.percentual !== '—' && <span className="tabular ml-1">({t.percentual})</span>}
    </span>
  );
}

/**
 * "Dispensar" da marca (`marca.dispensar`): confirmacao curta na propria linha. Some a marca ate o sugerido mudar de novo.
 * So existe quando a marca traz o id (a API o omite sem a permissao).
 */
function DispensarMarca({ id }: { id: string }) {
  const dispensar = useDispensarMarca();
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  if (!confirmando) {
    return (
      <button type="button" onClick={() => { setErro(null); setConfirmando(true); }} className="h-[30px] rounded-[9px] border border-warning-dot/50 bg-card px-3 font-medium text-warning-ink hover:bg-line-soft">
        Dispensar
      </button>
    );
  }
  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-caption">Dispensar este aviso? Ele volta só se o sugerido mudar de novo.</span>
        <button
          type="button"
          disabled={dispensar.isPending}
          onClick={() =>
            dispensar.mutate(id, {
              onSuccess: () => setConfirmando(false),
              onError: (e) => setErro(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API'),
            })
          }
          className="h-[30px] rounded-[9px] border border-warning-dot/50 bg-card px-3 font-medium text-warning-ink hover:bg-line-soft disabled:opacity-50"
        >
          Confirmar
        </button>
        <button type="button" onClick={() => setConfirmando(false)} className="h-[30px] rounded-[9px] px-3 font-medium text-ink-secondary hover:bg-line-soft">
          Cancelar
        </button>
      </div>
      {erro && (
        <span role="alert" className="text-caption text-danger-ink">
          {erro}
        </span>
      )}
    </div>
  );
}

/**
 * Aviso na Formacao: o sugerido desta chave mudou por custo e o praticado nao. `podeRegistrar` mostra o caminho (registrar
 * um novo praticado: foca o campo na propria tela); `podeDispensar` (`marca.dispensar`) mostra "Dispensar" com confirmacao.
 * Quem so le ve a marca e o novo sugerido.
 */
export function AvisoMarcaFormacao({
  marca,
  podeRegistrar,
  onRegistrar,
  podeDispensar = false,
}: {
  marca: MarcaRevisaoResponse;
  podeRegistrar: boolean;
  onRegistrar: () => void;
  podeDispensar?: boolean;
}) {
  const t = textosMarca(marca);
  return (
    <section role="status" aria-label="Revisar preço" data-testid="aviso-marca-formacao" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
      <div>
        <div className="font-semibold">Custo atualizado — revisar preço</div>
        <div className="mt-0.5">
          O sugerido mudou de <span className="tabular">{t.antes}</span> para <span className="tabular font-semibold">{t.depois}</span> (
          <span className="tabular">{t.variacao}</span>
          {t.percentual !== '—' && <span className="tabular"> · {t.percentual}</span>}) desde o último praticado registrado em {formatarDataHora(marca.desde)}. O praticado não muda sozinho.
        </div>
      </div>
      {(podeRegistrar || (podeDispensar && marca.id)) && (
        <span className="flex flex-wrap items-center justify-end gap-2">
          {podeRegistrar && (
            <button type="button" onClick={onRegistrar} className="h-[30px] rounded-[9px] border border-warning-dot/50 bg-card px-3 font-medium text-warning-ink hover:bg-line-soft">
              Registrar novo praticado
            </button>
          )}
          {podeDispensar && marca.id && <DispensarMarca id={marca.id} />}
        </span>
      )}
    </section>
  );
}
